import {
  BaseResponseProps,
  DetailItem,
  HeaderSection,
  IAppTableColumn,
  Label,
} from '@qlibs/react-components';
import { AssetProperties, initialAsset } from './types/asset.type';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate, useParams } from 'react-router-dom';
import { backToList } from '../../helpers/listFilterQuery';
import useDetailBreadcrumbs from '../../hooks/useDetailBreadcrumbs';
import React from 'react';
import { httpRequest } from '../../helpers/api';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Grid,
  Image,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  message,
} from 'antd';
import SectionContent from '../../components/SectionContent';
import { generateAddress } from '../../helpers/address';
import { ColumnsType } from 'antd/lib/table';
import { isRamus2AssetId } from '../../helpers/ramus2';
import {
  AssetMobileActionButton,
  AssetMobileAlert,
  AssetMobileDataTable,
  AssetMobileDetailLabel,
  AssetMobileDetailRow,
  AssetMobileDetailRows,
  AssetMobileDetailSection,
  AssetMobileDetailSectionHeader,
  AssetMobileDetailValue,
  AssetMobileImagePreview,
  AssetMobileNoImagePreview,
  AssetMobilePage,
  MobileAssetHeader,
} from './mobile';

const moment = require('moment');

interface ILocation {
  assetId: string;
}

interface ResponseProps
  extends BaseResponseProps<
    Omit<AssetProperties, 'createdAt' | 'updatedAt' | 'companies'>
  > {}

const AssetDetail = () => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { setBreadcrumbDetails } = useDetailBreadcrumbs();
  const { assetId } = useParams<keyof ILocation>() as ILocation;
  const isReadOnlyRamus2 = isRamus2AssetId(assetId);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [data, setData] = React.useState<AssetProperties>({
    ...initialAsset,
  });

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);

        const res = await httpRequest.get<ResponseProps>('/asset/' + assetId);
        if (!res) {
          message.error('Something went wrong');
          return;
        }

        setData({
          ...res.data.payload,
        });

        let label = `Detail Asset`;
        const bcDetails = [
          {
            field: 'assetId',
            value: assetId,
            label,
          },
        ];
        setBreadcrumbDetails(bcDetails);

        setIsLoading(false);
      } catch (error: any) {
        console.error(error);
        setIsLoading(false);
      }
    };

    fetchUser();

    // eslint-disable-next-line
  }, [assetId]);

  const handleClickEdit = () => {
    navigate('/asset/' + assetId + '/edit');
  };

  const _renderDimenstion = (
    length?: number,
    beam?: number,
    draught?: number
  ) => {
    let currLength = length;
    let currBeam = beam;
    let currDraught = draught;
    if (!length) {
      currLength = 0;
    }
    if (!beam) {
      currBeam = 0;
    }
    if (!beam) {
      currDraught = 0;
    }

    return `(${currLength} x ${currBeam} x ${currDraught}) Meters`;
  };

  const columns: ColumnsType<any> = [
    // {
    //   title: "Image",
    //   dataIndex: "imageLink",
    //   key: "imageLink",
    //   width: 70,
    //   render: (value: string, _: any) => (
    //     <Image
    //       width={60}
    //       height={60}
    //       style={{
    //         objectFit: "contain",
    //         border: "thin solid #ddd",
    //         overflow: "hidden",
    //         background: "#dadada",
    //         borderRadius: 4,
    //       }}
    //       src={value}
    //       fallback="/images/flag-not-found.png"
    //     />
    //   ),
    // },
    {
      title: 'Device Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Device Unique ID',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: string, _: any) => (
        <DetailItem
          label=''
          value={value.toUpperCase()}
          type='tag'
          _tagProps={{
            color: value === 'active' ? 'success' : 'error',
          }}
        />
      ),
    },
  ];

  const mobileDeviceColumns: ColumnsType<any> = [
    {
      title: 'Device Name',
      dataIndex: 'name',
      key: 'name',
      width: 220,
    },
    {
      title: 'Device Unique ID',
      dataIndex: 'uniqueId',
      key: 'uniqueId',
      width: 300,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: string) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
  ];

  const renderMobileDetailRow = (
    label: string,
    value: React.ReactNode,
    options?: { fullWidth?: boolean }
  ) => (
    <AssetMobileDetailRow $fullWidth={options?.fullWidth}>
      <AssetMobileDetailLabel>{label}</AssetMobileDetailLabel>
      <AssetMobileDetailValue>{value || 'N/A'}</AssetMobileDetailValue>
    </AssetMobileDetailRow>
  );

  const renderStatusTag = (status?: string) => (
    <Tag color={status === 'active' ? 'success' : 'error'}>
      {status === 'active' ? 'Active' : 'Inactive'}
    </Tag>
  );

  const renderMetadataRows = () => {
    const defaultMetadata = {
      showEventEngine: 0,
      showOperationMode: 0,
    };

    const effectiveMetadata = {
      ...defaultMetadata,
      ...(data.metadata || {}),
    };

    return Object.entries(effectiveMetadata)
      .filter(([_, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        const label = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase());
        const isBooleanValue = value === 0 || value === 1;

        return renderMobileDetailRow(
          label,
          isBooleanValue ? (
            <Tag color={value === 1 ? 'success' : 'default'}>
              {value === 1 ? 'ON' : 'OFF'}
            </Tag>
          ) : typeof value === 'object' ? (
            JSON.stringify(value)
          ) : (
            String(value)
          )
        );
      });
  };

  if (isMobile) {
    return (
      <AssetMobilePage>
        <MobileAssetHeader
          title="Asset"
          onBack={() => backToList(navigate, '/asset')}
          action={
            isReadOnlyRamus2 ? null : (
              <AssetMobileActionButton
                disabled={data.deletedAt ? true : false}
                type="primary"
                onClick={handleClickEdit}
              >
                Edit
              </AssetMobileActionButton>
            )
          }
        />

        <Spin spinning={isLoading}>
          {data?.deletedAt ? (
            <AssetMobileAlert>
              <Alert
                type="warning"
                message="This data has been archived and cannot be edited"
              />
            </AssetMobileAlert>
          ) : (
            false
          )}

          <AssetMobileDetailSection $bordered>
            <AssetMobileDetailSectionHeader>
              <h2>Information</h2>
              <p>Detail information about asset</p>
            </AssetMobileDetailSectionHeader>
            <AssetMobileDetailRows>
              <AssetMobileDetailRow $fullWidth>
                <AssetMobileDetailLabel>Image</AssetMobileDetailLabel>
                <AssetMobileDetailValue>
                  {data.imageLink ? (
                    <AssetMobileImagePreview
                      src={data.imageLink}
                      fallback="/images/empty-image.png"
                      preview={false}
                      $height={300}
                    />
                  ) : (
                    <AssetMobileNoImagePreview height={300} />
                  )}
                </AssetMobileDetailValue>
              </AssetMobileDetailRow>
              {renderMobileDetailRow('Name', data.name)}
              {renderMobileDetailRow('Company', data.company?.name || 'N/A')}
              {renderMobileDetailRow(
                'Created by',
                data?.userCreator?.name || 'N/A'
              )}
              {renderMobileDetailRow(
                'Created at',
                data.createdAt
                  ? moment(data.createdAt).format('DD MMMM YYYY, HH:mm')
                  : 'N/A'
              )}
              {renderMobileDetailRow(
                'Updated at',
                data.updatedAt
                  ? moment(data.updatedAt).format('DD MMMM YYYY, HH:mm')
                  : 'N/A'
              )}
              {renderMobileDetailRow('Status', renderStatusTag(data.status))}
            </AssetMobileDetailRows>
          </AssetMobileDetailSection>

          <AssetMobileDetailSection $bordered>
            <AssetMobileDetailSectionHeader>
              <h2>Information</h2>
              <p>These are asset information, you can change anything</p>
            </AssetMobileDetailSectionHeader>
            <AssetMobileDetailRows>
              {renderMobileDetailRow('PIC Name', data.picName || 'N/A')}
              {renderMobileDetailRow(
                'PIC Phone Number',
                data.picPhoneNumber || 'N/A'
              )}
              {renderMobileDetailRow('IMO Number', data.imoNumber || 'N/A')}
              {renderMobileDetailRow('MMSI Number', data.mmsiNumber || 'N/A')}
              {renderMobileDetailRow('Call Sign', data.callSign || 'N/A')}
              {renderMobileDetailRow('Ship Type', data.shipType || 'N/A')}
              {renderMobileDetailRow(
                'Dimension (Length x Beam x Draft/Draught)',
                _renderDimenstion(
                  data.dimensionLength,
                  data.dimensionBeam,
                  data.dimensionDraught
                ),
                { fullWidth: true }
              )}
              {renderMobileDetailRow(
                'Antenna Position',
                data.antennaPosition || 'N/A'
              )}
            </AssetMobileDetailRows>
          </AssetMobileDetailSection>

          <AssetMobileDetailSection $bordered>
            <AssetMobileDetailSectionHeader>
              <h2>Satellite & ID</h2>
              <p>These are asset information, you can change anything</p>
            </AssetMobileDetailSectionHeader>
            <AssetMobileDetailRows>
              {renderMobileDetailRow('GSM Number', data.gsmNumber || 'N/A')}
              {renderMobileDetailRow('IMEI Number', data.imeiNumber || 'N/A')}
              {renderMobileDetailRow('Satellite ID', data.satelliteId || 'N/A')}
              {renderMobileDetailRow(
                'Satellite Device ID',
                data.satelliteDeviceId || 'N/A'
              )}
              {renderMobileDetailRow(
                'Satellite Operator',
                data.satelliteOperator || 'N/A'
              )}
              {renderMobileDetailRow(
                'Installation Date',
                data.installationAt
                  ? moment(data.installationAt).format('DD MMMM YYYY, HH:mm')
                  : 'N/A'
              )}
            </AssetMobileDetailRows>
          </AssetMobileDetailSection>

          <AssetMobileDetailSection $bordered>
            <AssetMobileDetailSectionHeader>
              <h2>Devices</h2>
              <p>These are asset information, you can change anything</p>
            </AssetMobileDetailSectionHeader>
            <AssetMobileDataTable
              columns={mobileDeviceColumns}
              dataSource={data.devices}
              pagination={false}
              size="small"
              rowKey="uniqueId"
              scroll={{ x: 640 }}
            />
          </AssetMobileDetailSection>

          <AssetMobileDetailSection>
            <AssetMobileDetailSectionHeader>
              <h2>Active Metadata</h2>
              <p>Additional metadata associated with this asset</p>
            </AssetMobileDetailSectionHeader>
            <AssetMobileDetailRows>{renderMetadataRows()}</AssetMobileDetailRows>
          </AssetMobileDetailSection>
        </Spin>
      </AssetMobilePage>
    );
  }

  return (
    <React.Fragment>
      <HeaderSection
        icon='back'
        title={'Detail Asset'}
        subtitle={`Manage your asset data`}
        rightAction={
          isReadOnlyRamus2 ? false : (
            <Space>
              <Button
                disabled={data.deletedAt ? true : false}
                type='primary'
                onClick={handleClickEdit}
              >
                Edit Asset
              </Button>
            </Space>
          )
        }
      />

      <Spin spinning={isLoading}>
        <Card>
          {data?.deletedAt ? (
            <Alert
              className='mb-5'
              type='warning'
              message='This data has been archived and cannot be edited'
            />
          ) : (
            false
          )}
          <SectionContent
            groupTitle='Information'
            subTitle='Detail information about asset'
          >
            <Row>
              <Col span={24}>
                <Label value='Image' />
                <div
                  style={{
                    width: '100%',
                    height: 250,
                    background: '#f4f6f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                  }}
                >
                  <Image
                    width={'auto'}
                    height={250}
                    fallback='/images/empty-image.png'
                    src={data.imageLink}
                  />
                </div>
              </Col>
              <Col span={12}>
                <DetailItem label='Name' value={data.name} />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Company'
                  value={data.company?.name || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Created by'
                  value={data?.userCreator?.name || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Created at'
                  value={moment(data.createdAt).format('DD MMMM YYYY, HH:mm')}
                  // type='datetime'
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Updated at'
                  value={moment(data.updatedAt).format('DD MMMM YYYY, HH:mm')}
                  // type='datetime'
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Status'
                  value={data.status.toUpperCase()}
                  type='tag'
                  _tagProps={{
                    color: data.status === 'active' ? 'success' : 'error',
                  }}
                />
              </Col>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle='Information'
            subTitle='These are asset information, you can change anything'
          >
            <Row>
              <Col span={12}>
                <DetailItem label='PIC Name' value={data.picName || 'N/A'} />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='PIC Phone Number'
                  value={data.picPhoneNumber || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='IMO Number'
                  value={data.imoNumber || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='MMSI Number'
                  value={data.mmsiNumber || 'N/A'}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <DetailItem label='Call Sign' value={data.callSign || 'N/A'} />
              </Col>
              <Col span={12}>
                <DetailItem label='Ship Type' value={data.shipType || 'N/A'} />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <DetailItem
                  label='Dimension (Length x Beam x Draft/Draught)'
                  value={_renderDimenstion(
                    data.dimensionLength,
                    data.dimensionBeam,
                    data.dimensionDraught
                  )}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Antenna Position'
                  value={data.antennaPosition || 'N/A'}
                  // type={'datetime'}
                />
              </Col>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle='Satellite & ID'
            subTitle='These are asset information, you can change anything'
          >
            <Row>
              <Col span={12}>
                <DetailItem
                  label='GSM Number'
                  value={data.gsmNumber || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='IMEI Number'
                  value={data.imeiNumber || 'N/A'}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <DetailItem
                  label='Satellite ID'
                  value={data.satelliteId || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Satellite Device ID'
                  value={data.satelliteDeviceId || 'N/A'}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <DetailItem
                  label='Satellite Operator'
                  value={data.satelliteOperator || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <DetailItem
                  label='Installation Date'
                  value={
                    moment(data.installationAt).format('DD MMMM YYYY, HH:mm') ||
                    'N/A'
                  }
                  // type={'datetime'}
                />
              </Col>
            </Row>
          </SectionContent>

          <Divider />

          <SectionContent
            groupTitle='Devices'
            subTitle='These are asset information, you can change anything'
          >
            <Table
              size='small'
              columns={columns}
              dataSource={data.devices}
              pagination={false}
            />
          </SectionContent>

          <Divider />
          <SectionContent
            groupTitle='Active Metadata'
            subTitle='Additional metadata associated with this asset'
          >
            <Row gutter={[16, 16]}>
              {(() => {
                const DEFAULT_METADATA = {
                  showEventEngine: 0,
                  showOperationMode: 0,
                };

                const effectiveMetadata = {
                  ...DEFAULT_METADATA,
                  ...(data.metadata || {}),
                };

                return Object.entries(effectiveMetadata)
                  .filter(([_, value]) => value !== null && value !== undefined)
                  .map(([key, value]) => {
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    
                    const isBooleanValue = value === 0 || value === 1;
                    
                    const isEnabled = value === 1;
                    
                    return (
                      <Col span={12} key={key}>
                        {isBooleanValue ? (
                          <DetailItem
                            label={label}
                            value={isEnabled ? 'ON' : 'OFF'}
                            type='tag'
                            _tagProps={{
                              color: isEnabled ? 'success' : 'default',
                            }}
                          />
                        ) : (
                          <DetailItem
                            label={label}
                            value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          />
                        )}
                      </Col>
                    );
                  });
              })()}
            </Row>
          </SectionContent>
        </Card>
      </Spin>
    </React.Fragment>
  );
};

export default AssetDetail;
